import { useState } from 'react';
import { X, AlertCircle, Pause } from 'lucide-react';

interface PauseJobSessionModalProps {
  jobId: string;
  providerName: string;
  providerRole: string;
  jobValue: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, providerNotes: string, customerNotes: string, pausePayout: boolean) => void;
}

const PauseJobSessionModal = ({
  jobId,
  providerName,
  providerRole,
  jobValue,
  isOpen,
  onClose,
  onConfirm,
}: PauseJobSessionModalProps) => {
  const [pauseReason, setPauseReason] = useState('');
  const [providerNotes, setProviderNotes] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [pausePayout, setPausePayout] = useState(false);

  const pauseReasons = [
    'Weather conditions',
    'Equipment failure',
    'Material shortage',
    'Safety concerns',
    'Client request',
    'Site access issues',
    'Unexpected complications',
    'Other',
  ];

  const handleConfirm = () => {
    if (pauseReason) {
      onConfirm(pauseReason, providerNotes, customerNotes, pausePayout);
      handleClose();
    }
  };

  const handleClose = () => {
    setPauseReason('');
    setProviderNotes('');
    setCustomerNotes('');
    setPausePayout(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <Pause className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Pause Job Session</h2>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Current Provider Section */}
          <div className="bg-gradient-to-r from-teal-50 to-blue-50 border-l-4 border-teal-600 p-4 rounded">
            <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-3">Current Provider</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {providerName.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{providerName} • {providerRole}</p>
                  <p className="text-xs text-gray-600">Job ID: {jobId}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs font-medium text-gray-500 mb-1">Job Value</p>
                <p className="text-lg font-bold text-gray-900">{jobValue}</p>
              </div>
            </div>
          </div>

          {/* Required Pause Reason */}
          <div>
            <label className="block text-sm font-bold uppercase text-gray-500 tracking-wider mb-3">
              Required Pause Reason
            </label>
            <select
              value={pauseReason}
              onChange={(e) => setPauseReason(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
            >
              <option value="">Select a reason for pausing...</option>
              {pauseReasons.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>

          {/* Notes Section - Two Columns */}
          <div className="grid grid-cols-2 gap-4">
            {/* Notes for Provider */}
            <div>
              <label className="block text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">
                Notes for Provider
              </label>
              <textarea
                value={providerNotes}
                onChange={(e) => setProviderNotes(e.target.value)}
                placeholder="Internal instructions for the field team..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                rows={4}
              />
            </div>

            {/* Notes for Customer */}
            <div>
              <label className="block text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">
                Notes for Customer
              </label>
              <textarea
                value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value)}
                placeholder="Public explanation for the client dashboard..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                rows={4}
              />
            </div>
          </div>

          {/* Pause Payout Processing */}
          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <input
              type="checkbox"
              id="pausePayout"
              checked={pausePayout}
              onChange={(e) => setPausePayout(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 mt-1"
            />
            <div className="flex-1">
              <label htmlFor="pausePayout" className="block font-semibold text-gray-900 text-sm mb-1">
                Pause Payout Processing
              </label>
              <p className="text-xs text-gray-600">Hold all milestone payments during active pause state</p>
            </div>
          </div>

          {/* Admin Authority Required Warning */}
          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <p className="text-sm font-bold text-amber-700">● ADMIN AUTHORITY REQUIRED</p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={handleClose}
            className="px-6 py-2 text-teal-600 font-semibold hover:text-teal-700"
          >
            Discard Changes
          </button>
          <button
            onClick={handleConfirm}
            disabled={!pauseReason}
            className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Pause className="w-4 h-4" />
            Confirm Pause
          </button>
        </div>
      </div>
    </div>
  );
};

export default PauseJobSessionModal;
